"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SharedTrackingServiceV2 = exports.SUPPORTED_EXTENSIONS = exports.LinkAction = void 0;
const debugging_1 = require("./debugging");
const utils_1 = require("./utils");
const FEATURE_APP_NAME_UNKNOWN = 'unknown';
var LinkAction;
(function (LinkAction) {
    LinkAction["INTLINK"] = "internal_link";
    LinkAction["EXITLINK"] = "exit_link";
    LinkAction["DOWNLOAD"] = "download";
    LinkAction["CONTENT"] = "content";
})(LinkAction || (exports.LinkAction = LinkAction = {}));
exports.SUPPORTED_EXTENSIONS = [
    'doc',
    'docx',
    'eps',
    'xls',
    'ppt',
    'pptx',
    'pdf',
    'xlsx',
    'tab',
    'csv',
    'zip',
    'txt',
    'vsd',
    'vxd',
    'xml',
    'js',
    'css',
    'rar',
    'exe',
    'wma',
    'mov',
    'avi',
    'wmv',
    'mp3',
    'wav',
    'm4v',
    'ics',
];
class SharedTrackingServiceV2 {
    constructor(datalayer, sanitizeUrl, logger, primaryConsumerId) {
        this.datalayer = datalayer;
        this.sanitizeUrl = sanitizeUrl;
        this.logger = logger;
        this.primaryConsumerId = primaryConsumerId;
        this.impressionTrackingMap = new Map();
        this.observer = this.createObserver();
    }
    setFeatureAppName(featureApp) {
        const existingComponent = this.getComponent(featureApp.id);
        if (!existingComponent) {
            this.createComponent(featureApp);
            return;
        }
        existingComponent.componentInfo.componentName = featureApp.name;
    }
    track(featureApp, data, arrayBehavior) {
        var _a;
        const isPrimary = this.isPrimaryConsumer(featureApp.id);
        const { userUpdate, componentUpdate, pageUpdate } = data;
        if (componentUpdate) {
            this.updateComponent(featureApp, componentUpdate, arrayBehavior);
        }
        if (userUpdate) {
            this.updateUser(userUpdate);
        }
        if (pageUpdate) {
            this.updatePageInfo(featureApp, pageUpdate);
        }
        let { event } = data;
        if (isPrimary && ((_a = event.eventInfo) === null || _a === void 0 ? void 0 : _a.eventAction) === 'feature_app_ready') {
            event = (0, utils_1.mergeHandler)(event, {
                eventInfo: {
                    eventAction: 'page_load',
                },
            }, { merge: 'deep', arrayBehavior: 'replace' });
        }
        if (!event.eventInfo || typeof event.eventInfo.eventAction !== 'string') {
            this.logger.warn(`Dropping event with empty eventAction from ${featureApp.name} (id: ${featureApp.id}). This is most likely a bug in the feature app and should be fixed.`, event);
            return;
        }
        if (!this.datalayer.event) {
            this.datalayer.event = [];
        }
        this.datalayer.event.push(this.addEventAttributes(featureApp, event));
    }
    /**
     * Enables debug mode by adding the provided events to the debugLayer.
     * Logs the available events for debugging purposes.
     *
     * @param {Object.<string, (args?: any) => void>} events - An object where keys are event names and values are functions to be added to the debugLayer.
     * @param {string} featureAppId - The ID of the feature app for which debug mode is being enabled.
     */
    enableDebugMode(featureAppName, events) {
        if (this.datalayer.debug && this.datalayer.debug[featureAppName]) {
            return; // avoid adding the same events multiple times
        }
        if ((0, debugging_1.isDebugMode)() !== true) {
            return; // only enable debug mode if the query param is set
        }
        (0, debugging_1.debug)(`Debugging enabled for ${featureAppName}`);
        (0, debugging_1.debug)(`To access the debugging layer and fire events, use the digitalData.debug["${featureAppName}"] object on the window.`);
        (0, debugging_1.debug)(`Available debug events for featureAppName ${featureAppName} :`);
        debugging_1.debug.table(events);
        if (!this.datalayer.debug) {
            this.datalayer.debug = {
                [featureAppName]: {},
            }; // initialise the debugLayer
        }
        if (this.datalayer.debug) {
            this.datalayer.debug[featureAppName] = events;
        }
    }
    trackInternalNavigation(featureApp, data) {
        const event = (0, utils_1.mergeHandler)(data.event, {
            attributes: {
                targetURL: this.sanitizeUrl(`${window.location.origin}${data.targetHref}`),
            },
        }, { merge: 'deep', arrayBehavior: 'replace' });
        this.track(featureApp, Object.assign(Object.assign({}, data), { event }));
    }
    isPrimaryConsumer(featureAppId) {
        return this.primaryConsumerId === featureAppId;
    }
    updatePageInfo(featureApp, pageUpdate) {
        this.assertComponent(featureApp);
        const isPrimary = this.isPrimaryConsumer(featureApp.id);
        if (isPrimary) {
            this.setPageInfo(pageUpdate);
        }
    }
    updateComponent(featureApp, componentUpdate, arrayBehavior) {
        var _a;
        const isPrimary = this.isPrimaryConsumer(featureApp.id);
        if (isPrimary) {
            this.handlePrimaryConsumerComponentUpdate(componentUpdate, arrayBehavior !== null && arrayBehavior !== void 0 ? arrayBehavior : 'replace');
        }
        this.assertComponent(featureApp);
        const componentIndex = this.getComponentIndex(featureApp.id);
        const cleanUpdate = Object.assign(Object.assign({}, componentUpdate), { componentInfo: Object.assign({}, componentUpdate.componentInfo) });
        delete cleanUpdate.componentInfo.componentID;
        delete cleanUpdate.componentInfo.componentName;
        // datalayer.component is definitely defined after calling `assertComponent`
        this.datalayer.component[componentIndex] = ((0, utils_1.mergeHandler)(((_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a[componentIndex]) || {}, cleanUpdate, {
            merge: 'deep',
            arrayBehavior: arrayBehavior !== null && arrayBehavior !== void 0 ? arrayBehavior : 'replace',
        }));
    }
    handlePrimaryConsumerComponentUpdate(componentUpdate, arrayBehavior) {
        const { products, search, cart, page, user } = componentUpdate;
        if (products) {
            this.setProduct(products, arrayBehavior);
        }
        if (search) {
            this.setSearch(search);
        }
        if (cart) {
            this.setCart(cart);
        }
        if (page) {
            this.setPageInfo(page);
        }
        if (user) {
            this.updateUser(user);
        }
    }
    setProduct(productsUpdate, arrayBehavior) {
        var _a;
        this.datalayer.products = ((0, utils_1.mergeHandler)(((_a = this.datalayer).products || (_a.products = {})), productsUpdate, {
            merge: 'first-level',
            arrayBehavior,
        }));
    }
    setSearch(searchUpdate) {
        var _a;
        this.datalayer.search = ((0, utils_1.mergeHandler)(((_a = this.datalayer).search || (_a.search = {})), searchUpdate, { merge: 'replace' }));
    }
    setCart(cartUpdate) {
        this.datalayer.cart = ((0, utils_1.mergeHandler)(this.datalayer.cart, cartUpdate, { merge: 'replace' }));
    }
    setPageInfo(pageUpdate) {
        var _a, _b, _c, _d, _e, _f;
        // allows us to deep merge override any unsanitized urls
        const sanitizedUrlPageUpdate = {
            pageInfo: {},
        };
        // Sanitize and set the destinationURL if it exists
        if ((_a = pageUpdate.pageInfo) === null || _a === void 0 ? void 0 : _a.destinationURL) {
            sanitizedUrlPageUpdate.pageInfo.destinationURL = this.sanitizeUrl(pageUpdate.pageInfo.destinationURL);
        }
        // Sanitize and set the referringURL if it exists
        if ((_b = pageUpdate.pageInfo) === null || _b === void 0 ? void 0 : _b.referringURL) {
            sanitizedUrlPageUpdate.pageInfo.referringURL = this.sanitizeUrl(pageUpdate.pageInfo.referringURL);
        }
        else if ((_c = pageUpdate.pageInfo) === null || _c === void 0 ? void 0 : _c.destinationURL) {
            // If referringURL is not set, make the current destinationURL the referringURL
            sanitizedUrlPageUpdate.pageInfo.referringURL = (_e = (_d = this.datalayer.page) === null || _d === void 0 ? void 0 : _d.pageInfo) === null || _e === void 0 ? void 0 : _e.destinationURL;
        }
        this.datalayer.page = (0, utils_1.mergeHandler)((_f = this.datalayer.page) !== null && _f !== void 0 ? _f : {}, (0, utils_1.mergeHandler)(pageUpdate, sanitizedUrlPageUpdate, {
            merge: 'deep',
            arrayBehavior: 'replace',
        }), { merge: 'deep', arrayBehavior: 'replace' });
    }
    updateUser(userUpdate) {
        if (!this.datalayer.user) {
            this.datalayer.user = [{ segment: {} }];
        }
        this.datalayer.user[0].segment = ((0, utils_1.mergeHandler)(this.datalayer.user[0].segment, userUpdate, { merge: 'shallow' }));
        // whatever is passed from the fa will populate the segment
        // user = [{ segment: { isLogged: true, myAudiId: "bla"} }];
    }
    // eslint-disable-next-line class-methods-use-this
    evaluateLinkType(targetUrl) {
        if (!targetUrl) {
            return LinkAction.CONTENT;
        }
        const urlLower = targetUrl.toLowerCase();
        if (exports.SUPPORTED_EXTENSIONS.some((ext) => urlLower.includes(`.${ext}`))) {
            return LinkAction.DOWNLOAD;
        }
        if (targetUrl.startsWith('/')) {
            return LinkAction.INTLINK;
        }
        if (targetUrl.startsWith('http')) {
            if (targetUrl.startsWith(window.location.origin)) {
                return LinkAction.INTLINK;
            }
            return LinkAction.EXITLINK;
        }
        return undefined;
    }
    registerImpressionTracking(featureAppInfo, element, dataCallback) {
        const trackingInfo = {
            featureAppInfo,
            dataCallback,
            fired: false,
            observer: {
                Observer70: { isVisible: false },
                Observer100: { isVisible: false },
            },
        };
        if (!this.impressionTrackingMap.has(element)) {
            this.impressionTrackingMap.set(element, trackingInfo);
            this.observer.observe(element);
        }
        else {
            throw new Error(`The element of the feature app ${featureAppInfo.name} (id: ${featureAppInfo.id}) is already registered: ${element} `);
        }
        return () => {
            this.removeImpressionObservers(element);
            this.impressionTrackingMap.delete(element);
        };
    }
    getDatalayer() {
        return this.datalayer;
    }
    getComponentIndex(featureAppId) {
        var _a, _b;
        return ((_b = (_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a.findIndex((component) => { var _a; return ((_a = component === null || component === void 0 ? void 0 : component.componentInfo) === null || _a === void 0 ? void 0 : _a.componentID) === featureAppId; })) !== null && _b !== void 0 ? _b : -1);
    }
    getComponent(featureAppId) {
        var _a;
        return (_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a[this.getComponentIndex(featureAppId)];
    }
    assertComponent(featureApp) {
        if (this.getComponent(featureApp.id)) {
            return;
        }
        throw new Error(`Component for feature app with ID ${featureApp.id} does not exist. Did you forget to set trackingService.featureAppName?`);
    }
    createComponent(featureApp) {
        if (this.getComponent(featureApp.id)) {
            return;
        }
        if (!this.datalayer.component) {
            this.datalayer.component = [];
        }
        this.datalayer.component.push({
            componentInfo: {
                componentID: featureApp.id,
                componentName: featureApp.name || FEATURE_APP_NAME_UNKNOWN,
                isPrimaryConsumer: this.isPrimaryConsumer(featureApp.id),
            },
        });
    }
    removeComponent(featureAppId) {
        var _a;
        const componentIndex = this.getComponentIndex(featureAppId);
        if (componentIndex >= 0) {
            (_a = this.datalayer.component) === null || _a === void 0 ? void 0 : _a.splice(componentIndex, 1);
        }
    }
    addEventAttributes(featureApp, event) {
        var _a;
        this.assertComponent(featureApp);
        const currentURL = this.sanitizeUrl(window.location.href);
        const automaticAttributes = {
            currentURL,
            relatedComponent: {
                featureAppID: featureApp.id,
                featureAppName: featureApp.name || FEATURE_APP_NAME_UNKNOWN,
                componentIndex: this.getComponentIndex(featureApp.id),
            },
        };
        const fullAttributes = ((0, utils_1.mergeHandler)((_a = event.attributes) !== null && _a !== void 0 ? _a : {}, automaticAttributes, {
            merge: 'first-level',
            arrayBehavior: 'replace',
        }));
        return Object.assign(Object.assign({}, event), { attributes: fullAttributes });
    }
    createObserver() {
        /* This observer should trigger at any intersection in the upper 70%.
         * With the -30% in the rootMargin we reduce the intersection space to the bottom
         * and only the upper 70% of the space will trigger the observer.
         */
        const observer70 = new IntersectionObserver(this.visibilityCallback.bind(this, 'Observer70'), {
            root: null,
            rootMargin: '0px 0px -30% 0px',
            threshold: 0,
        });
        const observer100 = new IntersectionObserver(this.visibilityCallback.bind(this, 'Observer100'), {
            root: null,
            rootMargin: '0px',
            threshold: 1,
        });
        return {
            observe(node) {
                observer70.observe(node);
                observer100.observe(node);
            },
            unobserve(node) {
                observer70.unobserve(node);
                observer100.unobserve(node);
            },
        };
    }
    visibilityCallback(observerType, entries) {
        const delay = 1000;
        entries.forEach((intersectionEntry) => {
            let isVisible = intersectionEntry.intersectionRatio > 0;
            if (observerType === 'Observer100') {
                isVisible = intersectionEntry.intersectionRatio === 1;
            }
            const element = intersectionEntry.target;
            const elementTrackingInfo = this.impressionTrackingMap.get(element);
            if (!elementTrackingInfo || elementTrackingInfo.fired) {
                this.removeImpressionObservers(element);
                return;
            }
            const currentObserverData = elementTrackingInfo.observer[observerType];
            currentObserverData.isVisible = isVisible;
            if (isVisible && !currentObserverData.timeoutId) {
                currentObserverData.timeoutId = setTimeout(this.triggerTracking.bind(this, element, observerType), delay);
            }
            else if (!isVisible && currentObserverData.timeoutId) {
                clearTimeout(currentObserverData.timeoutId);
                currentObserverData.timeoutId = undefined;
            }
        });
    }
    triggerTracking(element, observerType) {
        const trackingInfo = this.impressionTrackingMap.get(element);
        if (trackingInfo && !trackingInfo.fired && trackingInfo.observer[observerType].isVisible) {
            trackingInfo.fired = true;
            this.removeImpressionObservers(element);
            this.track(trackingInfo.featureAppInfo, trackingInfo.dataCallback());
        }
        else if (!trackingInfo) {
            this.logger.warn(`Could not find tracking info for the element: '${element}'`);
        }
    }
    removeImpressionObservers(element) {
        this.observer.unobserve(element);
        const trackingInfo = this.impressionTrackingMap.get(element);
        if (trackingInfo) {
            Object.values(trackingInfo.observer).forEach((o) => {
                if (o.timeoutId) {
                    clearTimeout(o.timeoutId);
                    // eslint-disable-next-line no-param-reassign
                    o.timeoutId = undefined;
                }
            });
        }
    }
}
exports.SharedTrackingServiceV2 = SharedTrackingServiceV2;
//# sourceMappingURL=shared.js.map